import React, { useState, useMemo, useEffect } from 'react';
import * as CONSTANTS from "common/utils/data_validations"
import * as Yup from "yup"
import { useFormik } from "formik"
import { Form, Row, Col, Input, FormFeedback, Label } from 'reactstrap';
import Select from 'react-select';
import { getOptionFromValueForSelectBox, getPrimaryButtonClass } from 'helpers/sf_utils';
import { MODE, LEAD_INTEGRATION_STATUS, PRODUCT_TYPE_IDS, REQUIRED_FEATURE_FLAGS } from 'helpers/sf_constants';
import LabelWithTooltip from 'components/Common/LabelWithTooltip';
import PingIntegration from 'components/integrations/PingIntegration';
import useFeatureFlagsForAccounts from 'hooks/useFeatureFlagsForAccounts';
import { useSelector } from 'react-redux';
import ToastAlert, { ToastAlertIconType } from 'components/Common/ToastAlert/ToastAlert';

const STR_INTEGRATION_COMPONENT_UPDATING_MSG = 'Integration component is updating.'

const integrationTypeOptions = [{
  options: [
    { label: "Click", value: PRODUCT_TYPE_IDS.CLICKS },
    { label: "Lead", value: PRODUCT_TYPE_IDS.LEADS },
    { label: "Call", value: PRODUCT_TYPE_IDS.CALLS }
  ]
}]

export const TOOLTIP_CONSTANTS = {
  NAME_FIELD: {
    ID: "connector_tooltip_name_field",
    NAME: "Name",
    TOOLTIP_MESSAGE: "Create a name for your connector",
  },
  TEMPLATE: {
    ID: "connector_tooltip_template_field",
    NAME: "Select Connector From Template",
    TOOLTIP_MESSAGE: "Starting with a template can make setup easier",
  },
  CONNECTION_TYPE: {
    ID: "connector_tooltip_connection_type_field",
    NAME: "Connection Type",
    TOOLTIP_MESSAGE: "Type of connection can be Post, Get, Put, or Delete",
  },
  POSTING_URL: {
    ID: "connector_tooltip_posting_url_field",
    NAME: "Posting URL",
    TOOLTIP_MESSAGE: "Destination of a web server that accepts the data enclosed in the body of the request message",
  },
  API_RESPONSE_TYPE: {
    ID: "connector_tooltip_api_response_type_field",
    NAME: "Api Response Type",
    TOOLTIP_MESSAGE: "Type of response of your API. We currently only support json and xml based APIs",
  },
  BID_TYPES: {
    ID: "connector_tooltip_bid_types_field",
    NAME: "Bid Types",
    TOOLTIP_MESSAGE: " The type of bids your API responds with.",
  },
  PING_ID: {
    ID: "connector_tooltip_ping_id_field",
    NAME: "Ping ID",
    TOOLTIP_MESSAGE: "Exact field name in your API response that contains ping ID",
  },
  BID: {
    ID: "connector_tooltip_bid_field",
    NAME: "Bid",
    TOOLTIP_MESSAGE: "Exact field name in your API response that contains bid",
  },
  BUYER_NAME: {
    ID: "connector_tooltip_buyer_name_field",
    NAME: "Buyer Name",
    TOOLTIP_MESSAGE: "Exact field name in your API response that contains buyer name. We default to independent agency if not present",
  },
  BUYERS: {
    ID: "connector_tooltip_buyers_field",
    NAME: "Buyers",
    TOOLTIP_MESSAGE: "Exact field name in your API response that contains buyers/legs",
  },
  BID_ID: {
    ID: "connector_tooltip_bid_id_field",
    NAME: "Bid ID",
    TOOLTIP_MESSAGE: "Exact field name in your API response that contains bid ID for that leg",
  },
  LEG_TYPE: {
    ID: "connector_tooltip_leg_type_field",
    NAME: "Leg Type",
    TOOLTIP_MESSAGE: "Exact field name in your API response that contains leg type",
  },
  PHASE: {
    ID: 'connector_tooltip_phase',
    NAME: 'Phase',
    TOOLTIP_MESSAGE: 'Phase of the integration. Pre-Ping: Happens before ping phase and used to check if there is coverage. Ping: Sell lead after acknowledgement. Post: Integration processing while lead is being sold.  Pre-fill: Prefill Integration (for click campaigns only).'
  },
  BILLABLE_DURATION: {
    ID: 'connector_tooltip_billable_duration',
    NAME: 'Billable Duration',
    TOOLTIP_MESSAGE: 'Exact field name in your API response that returns billable duration.'
  },
  TRANSFER_NUMBER: {
    ID: 'connector_tooltip_transfer_number',
    NAME: 'Transfer Number',
    TOOLTIP_MESSAGE: 'Exact field name in your API response that returns transfer number.'
  },
  MIN_BID: {
    ID: 'connector_tooltip_minimum_bid',
    NAME: 'Minimum Bid',
    TOOLTIP_MESSAGE: 'Minimum bid to be considered for auction. If the ping API responds with bid less than minimum bid, we will not consider that bid for the auction.'
  },
  CLICK_URL: {
    ID: "connector_tooltip_click_url",
    NAME: "Click URL",
    TOOLTIP_MESSAGE: "Click URL" // TODO: get tooltip message
  },
  TITLE: {
    ID: "connector_tooltip_title",
    NAME: "Title",
    TOOLTIP_MESSAGE: "Title" // TODO: get tooltip message
  },
  DESCRIPTION: {
    ID: "connector_tooltip_description",
    NAME: "Description",
    TOOLTIP_MESSAGE: "Description" // TODO: get tooltip message
  },
  LOGO_URL: {
    ID: "connector_tooltip_logo_url",
    NAME: "Logo URL",
    TOOLTIP_MESSAGE: "Logo URL" // TODO: get tooltip message
  },
  SITE_HOST: {
    ID: "connector_tooltip_site_host",
    NAME: "Site Host",
    TOOLTIP_MESSAGE: "Site Host" // TODO: get tooltip message
  },
  COMPANY_NAME: {
    ID: "connector_tooltip_company_name",
    NAME: "Company Name",
    TOOLTIP_MESSAGE: "Company Name" // TODO: get tooltip message
  },
  DISPLAY_NAME: {
    ID: "connector_tooltip_display_name",
    NAME: "Display Name",
    TOOLTIP_MESSAGE: "Display Name" // TODO: get tooltip message
  },
  IMPRESSION_PIXEL: {
    ID: "connector_tooltip_impression_pixel",
    NAME: "Impression Pixel",
    TOOLTIP_MESSAGE: "Impression Pixel"
  },
  BASE_URL: {
    ID: "connector_tooltip_base_url",
    NAME: "Base URL",
    TOOLTIP_MESSAGE: "Base URL for the click integration in pre-fill phase"
  },
  FOLLOW_REDIRECT: {
    ID: "follow_redirect_tooltip",
    NAME: "Follow Redirect",
    TOOLTIP_MESSAGE: "When a server sends a redirect status code (e.g., 301 permanent or 302 temporary), we will automatically follow the new URL."
  },
  PREMIUM: {
    ID: "premium_tooltip",
    NAME: "Premium",
    TOOLTIP_MESSAGE: "Total Premium"
  },
  TERM: {
    ID: "term_tooltip",
    NAME: "Term",
    TOOLTIP_MESSAGE: "Premium Term (Numbers only)"
  }
}

export const LEAD_INTEGRATION_PHASE = {
  PRE_PING: 'pre-ping',
  PING: 'ping',
  POST: 'post',
  PRE_FILL: 'pre_fill',
  ACCEPT_REJECT: 'Accept-Reject',
  DIRECT_POST: 'Direct Post'
}

export const optionsConnectionType = [{
  options: [
    { label: "POST", value: "POST" },
    { label: "GET", value: "GET" },
    { label: "PUT", value: "PUT" },
    { label: "DELETE", value: "DELETE" },
  ]
}];

export const optionsApiResponseTypes = [{
  options: [
    { label: "JSON", value: "json" },
    { label: "XML", value: "xml" },
    { label: 'Text', value: 'text' }
  ]
}];

export const optionsLeadIntegrationPhase = [{
  options: [
    { label: 'Pre-Ping', value: LEAD_INTEGRATION_PHASE.PRE_PING },
    { label: 'Ping', value: LEAD_INTEGRATION_PHASE.PING },
    { label: 'Post', value: LEAD_INTEGRATION_PHASE.POST },
    { label: 'Pre-fill', value: LEAD_INTEGRATION_PHASE.PRE_FILL }
  ]
}];

export const CONSTANTS_FOR_BID_TYPES = {
  SHARED: "shared",
  EXCLUSIVE: "exclusive",
  MIXED: "mixed",
}

export const optionsBidTypes = [{
  options: [
    { label: "Shared", value: CONSTANTS_FOR_BID_TYPES.SHARED },
    { label: "Exclusive", value: CONSTANTS_FOR_BID_TYPES.EXCLUSIVE },
    { label: "Mixed", value: CONSTANTS_FOR_BID_TYPES.MIXED },
  ]
}];

/**
 * @param {object} initialState from props
 * @param {function} onSubmitState from props
 * @param {function} openNextAccordion from props
 * @returns
 */
const Connector = ({ mode, initialState, onSubmitState, openNextAccordion, leadIntegrationTemplates, leadIntegrationTemplatesLoading, onTemplateSelected }) => {
  const [isFeatureFlagActiveForAccounts] = useFeatureFlagsForAccounts();

  const { current_account } = useSelector((state) => ({
    current_account: state.accounts.current_account,
  }));

  const isStaticBidsFeatureEnabled = isFeatureFlagActiveForAccounts(
    REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_STATIC_BIDS_INTEGRATION,
    current_account
  );

  const optionsConnectorFromTemplate = useMemo(() => {
    return (leadIntegrationTemplates || []).map(template => ({ label: template.name, value: template.id }))
  }, [leadIntegrationTemplates]);

  const [selectedConnectorFromTemplate, setSelectedConnectorFromTemplate] = useState(null);
  const selectedTemplateState = useMemo(() => {
    if (!selectedConnectorFromTemplate?.value) return null;
    return (leadIntegrationTemplates || []).find(el => el.id === selectedConnectorFromTemplate.value) || null;
  }, [leadIntegrationTemplates, selectedConnectorFromTemplate]);
  const isTemplateReadyForCreate = useMemo(() => {
    if (!selectedConnectorFromTemplate?.value) return true;
    return Array.isArray(selectedTemplateState?.lead_integration_req_payloads);
  }, [selectedConnectorFromTemplate, selectedTemplateState]);

  const applyTemplateStateToForm = (selectedState) => {
    if (!selectedState) return

    if (validation.values?.name.trim() === "") {
      validation.handleChange({ target: { value: selectedState.name, name: "name" } })
    }

    validation.handleChange({ target: { value: selectedState.posting_url || "", name: "posting_url" } })
    validation.handleChange({ target: { value: selectedState?.is_ping || false, name: "is_ping" } })

    // Handle req_method dropdown
    if (selectedState?.req_method) {
      const reqMethodOption = getOptionFromValueForSelectBox(optionsConnectionType[0].options, selectedState.req_method)
      validation.handleChange({ target: { value: reqMethodOption, name: "req_method" } })
    }

    // Handle product_type_id dropdown
    if (selectedState?.product_type_id) {
      const productTypeOption = getOptionFromValueForSelectBox(integrationTypeOptions[0].options, selectedState.product_type_id)
      validation.handleChange({ target: { value: productTypeOption, name: "product_type_id" } })
    }

    // Handle lead_integration_phase dropdown
    if (selectedState?.phase) {
      const phaseOption = getOptionFromValueForSelectBox(optionsLeadIntegrationPhase[0].options, selectedState.phase)
      validation.handleChange({ target: { value: phaseOption, name: "lead_integration_phase" } })
    }

    const parsedConfigObj = JSON.parse(selectedState?.ping_config || "{}")

    if (parsedConfigObj?.api_response_type) {
      const apiResponseOption = getOptionFromValueForSelectBox(optionsApiResponseTypes[0].options, parsedConfigObj.api_response_type)
      validation.handleChange({ target: { value: apiResponseOption, name: "api_response_type" } })
    }
    if (parsedConfigObj?.bid_types) {
      const bidTypesOption = getOptionFromValueForSelectBox(optionsBidTypes[0].options, parsedConfigObj.bid_types)
      validation.handleChange({ target: { value: bidTypesOption, name: "bid_types" } })
    }

    validation.handleChange({ target: { value: parsedConfigObj?.ping_id || "", name: "ping_id" } })
    validation.handleChange({ target: { value: parsedConfigObj?.bid || "", name: "bid" } })
    validation.handleChange({ target: { value: parsedConfigObj?.buyer_name || "", name: "buyer_name" } })
    validation.handleChange({ target: { value: parsedConfigObj?.buyers || "", name: "buyers" } })
    validation.handleChange({ target: { value: parsedConfigObj?.bid_id || "", name: "bid_id" } })
    validation.handleChange({ target: { value: parsedConfigObj?.leg_type || "", name: "leg_type" } })
    validation.handleChange({ target: { value: parsedConfigObj?.billable_duration || "", name: "billable_duration" } })
    validation.handleChange({ target: { value: parsedConfigObj?.transfer_number || "", name: "transfer_number" } })
    validation.handleChange({ target: { value: parsedConfigObj?.min_bid || "", name: "min_bid" } })
    validation.handleChange({ target: { value: parsedConfigObj?.click_url || "", name: "click_url" } })
    validation.handleChange({ target: { value: parsedConfigObj?.title || "", name: "title" } })
    validation.handleChange({ target: { value: parsedConfigObj?.description || "", name: "description" } })
    validation.handleChange({ target: { value: parsedConfigObj?.logo_url || "", name: "logo_url" } })
    validation.handleChange({ target: { value: parsedConfigObj?.site_host || "", name: "site_host" } })
    validation.handleChange({ target: { value: parsedConfigObj?.company_name || "", name: "company_name" } })
    validation.handleChange({ target: { value: parsedConfigObj?.display_name || "", name: "display_name" } })
    validation.handleChange({ target: { value: parsedConfigObj?.impression_pixel || "", name: "impression_pixel" } })
    validation.handleChange({ target: { value: parsedConfigObj?.follow_redirect || false, name: "follow_redirect" } })
    validation.handleChange({ target: { value: parsedConfigObj?.term || "", name: "term" } })
    validation.handleChange({ target: { value: parsedConfigObj?.premium || "", name: "premium" } })
    validation.handleChange({ target: { value: parsedConfigObj?.base_url || "", name: "base_url" } })
    if (isStaticBidsFeatureEnabled) {
      validation.handleChange({ target: { value: parsedConfigObj?.static_bids || false, name: "static_bids" } })
    }
  }

  const handleConnectorFromTemplate = (option) => {
    if (!leadIntegrationTemplates || !option) return
    setSelectedConnectorFromTemplate(option)
    onTemplateSelected?.(option.value)
    const selectedState = leadIntegrationTemplates.find(el => el.id === option.value)
    applyTemplateStateToForm(selectedState)
  }

  useEffect(() => {
    if (!selectedConnectorFromTemplate?.value) return
    const selectedState = leadIntegrationTemplates?.find(el => el.id === selectedConnectorFromTemplate.value)
    if (!selectedState) return
    applyTemplateStateToForm(selectedState)
  }, [selectedConnectorFromTemplate, leadIntegrationTemplates]);

  const integration_ping_config = useMemo(() => {
    if (initialState?.lead_integration?.ping_config) {
      return JSON.parse(initialState?.lead_integration?.ping_config || "{}")
    }
    return null
  }, [initialState?.lead_integration?.ping_config])

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: initialState?.lead_integration?.name || "",
      req_method: getOptionFromValueForSelectBox(optionsConnectionType[0].options, initialState?.lead_integration?.req_method) || null,
      posting_url: initialState?.lead_integration?.posting_url || "",
      product_type_id: mode === MODE.CREATE ? integrationTypeOptions[0].options.find((el => el.value === PRODUCT_TYPE_IDS.LEADS)) : getOptionFromValueForSelectBox(integrationTypeOptions[0].options, initialState?.lead_integration?.product_type_id) || null,
      is_ping: initialState?.lead_integration?.is_ping || false,
      api_response_type: getOptionFromValueForSelectBox(optionsApiResponseTypes[0].options, integration_ping_config?.api_response_type) || null,
      bid_types: getOptionFromValueForSelectBox(optionsBidTypes[0].options, integration_ping_config?.bid_types) || null,
      ping_id: integration_ping_config?.ping_id || "",
      bid: integration_ping_config?.bid || "",
      buyer_name: integration_ping_config?.buyer_name || "",
      buyers: integration_ping_config?.buyers || "",
      bid_id: integration_ping_config?.bid_id || "",
      leg_type: integration_ping_config?.leg_type || "",
      lead_integration_phase: getOptionFromValueForSelectBox(optionsLeadIntegrationPhase[0].options, initialState?.lead_integration?.phase) || null,
      billable_duration: integration_ping_config?.billable_duration || "",
      transfer_number: integration_ping_config?.transfer_number || "",
      min_bid: integration_ping_config?.min_bid || "",
      click_url: integration_ping_config?.click_url || "",
      title: integration_ping_config?.title || "",
      description: integration_ping_config?.description || "",
      logo_url: integration_ping_config?.logo_url || "",
      site_host: integration_ping_config?.site_host || "",
      company_name: integration_ping_config?.company_name || "",
      display_name: integration_ping_config?.display_name || "",
      impression_pixel: integration_ping_config?.impression_pixel || "",
      premium: integration_ping_config?.premium || "",
      follow_redirect: integration_ping_config?.follow_redirect || false,
      term: integration_ping_config?.term || "",
      base_url: integration_ping_config?.base_url || "",
      ...(isStaticBidsFeatureEnabled && { static_bids: integration_ping_config?.static_bids || false }),
    },
    validationSchema: Yup.object({
      name: Yup.string().required(),
      req_method: Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
      posting_url: Yup.string()
        .required(CONSTANTS.STR_REQUIRED_ADCREATIVE_URL),
      api_response_type: Yup.object().nullable().when("is_ping", {
        is: true,
        then: Yup.object().shape({
          label: Yup.string().required(),
          value: Yup.string().required()
        })
      }),
      ping_id: Yup.string().when("is_ping", {
        is: true,
        then: Yup.string().required()
      }),
      bid: Yup.string().when("is_ping", {
        is: true,
        then: Yup.string().required(),
      }),
      lead_integration_phase: Yup.object().nullable("phase", {
        is: true,
        then: Yup.object().shape(({
          label: Yup.string().required(),
          value: Yup.string().required()
        }))
      })
    }),
    onSubmit: (values, { resetForm }) => {
      const lead_integration = {
        "name": values?.name,
        "req_method": values?.req_method?.value || "",
        "posting_url": values?.posting_url || "",
        "is_ping": values?.is_ping || false,
        "ping_pii": values?.is_ping ? (initialState?.lead_integration?.ping_pii || false) : false,
        "phase": values?.is_ping ? (values?.lead_integration_phase?.value || null) : null,
        "product_type_id": values?.is_ping ? (values?.product_type_id?.value || null) : null,
        "ping_config": JSON.stringify({
          "api_response_type": values?.api_response_type?.value || "",
          "bid_types": values?.bid_types?.value || "",
          "ping_id": values?.ping_id || "",
          "bid": values?.bid || "",
          "buyer_name": values?.buyer_name || "",
          "buyers": values?.buyers || "",
          "bid_id": values?.bid_id || "",
          "leg_type": values?.leg_type || "",
          "billable_duration": values?.billable_duration || "",
          "transfer_number": values?.transfer_number || "",
          "min_bid": values?.min_bid || "",
          "click_url": values?.click_url || "",
          "title": values?.title || "",
          "description": values?.description || "",
          "logo_url": values?.logo_url || "",
          "site_host": values?.site_host || "",
          "company_name": values?.company_name || "",
          "display_name": values?.display_name || "",
          "impression_pixel": values?.impression_pixel || "",
          "premium": values?.premium || "",
          "follow_redirect": values?.follow_redirect || false,
          "term": values?.term || "",
          "base_url": values?.base_url || "",
          ...(isStaticBidsFeatureEnabled && { "static_bids": values?.static_bids || false }),
        })
      }

      if (mode === MODE.EDIT) {
        onSubmitState({ lead_integration })
      }
      if (mode === MODE.CREATE) {
        if (!isTemplateReadyForCreate) {
          ToastAlert.fire({ icon: ToastAlertIconType.WARNING, title: STR_INTEGRATION_COMPONENT_UPDATING_MSG });
          return;
        }
        if (selectedConnectorFromTemplate?.value != null) {
          const selectedState = leadIntegrationTemplates.find(el => el.id === selectedConnectorFromTemplate.value)
          onSubmitState(
            {
              lead_integration:
              {
                ...selectedState,
                ...lead_integration,
                id: null,
                status: LEAD_INTEGRATION_STATUS.DRAFT,
              }
            }
          );
        } else {
          onSubmitState({ ...initialState, lead_integration: { ...initialState?.lead_integration, ...lead_integration, lead_integration_macros_ui: true } });
        }
      }
      openNextAccordion();
      resetForm();
    },
  });

  return (
    <React.Fragment>
      <Form
        onSubmit={e => {
          e.preventDefault()
          validation.handleSubmit()
          return false
        }}
      >
        <Row>
          <Col md={8} xl={8}>
            <Row>
              <Col md={12} xl={12}>
                <div className="mb-3">
                  <LabelWithTooltip name={TOOLTIP_CONSTANTS.NAME_FIELD.NAME} tooltip_message={TOOLTIP_CONSTANTS.NAME_FIELD.TOOLTIP_MESSAGE} target={TOOLTIP_CONSTANTS.NAME_FIELD.ID} htmlFor="name" />
                  <Input
                    className="form-control"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    name="name"
                    value={validation.values.name}
                    invalid={
                      validation.touched.name &&
                        validation.errors.name
                        ? true
                        : false
                    }
                  />
                  {validation.touched.name &&
                    validation.errors.name ? (
                    <span className="text-danger">
                      {CONSTANTS.STR_REQUIRED_FIELD}
                    </span>
                  ) : null}
                </div>
              </Col>
              {(mode === MODE.CREATE) && (
                <Col md={12} xl={12}>
                  <div className="mb-3">
                    <LabelWithTooltip name={TOOLTIP_CONSTANTS.TEMPLATE.NAME} tooltip_message={TOOLTIP_CONSTANTS.TEMPLATE.TOOLTIP_MESSAGE} target={TOOLTIP_CONSTANTS.TEMPLATE.ID} htmlFor="req_payload_template" />
                    <Select
                      options={optionsConnectorFromTemplate}
                      classNamePrefix="select2-selection"
                      onChange={option => {
                        handleConnectorFromTemplate(option)
                      }}
                      value={selectedConnectorFromTemplate}
                      isLoading={leadIntegrationTemplatesLoading}
                      isDisabled={leadIntegrationTemplatesLoading}
                    />
                  </div>
                </Col>
              )}
              <Col md={12} xl={12}>
                <div className="mb-3">
                  <LabelWithTooltip name={TOOLTIP_CONSTANTS.CONNECTION_TYPE.NAME} tooltip_message={TOOLTIP_CONSTANTS.CONNECTION_TYPE.TOOLTIP_MESSAGE} target={TOOLTIP_CONSTANTS.CONNECTION_TYPE.ID} htmlFor="connectio-type" />
                  <Select
                    options={optionsConnectionType}
                    classNamePrefix="select2-selection"
                    onChange={option => {
                      let event = {
                        target: {
                          name: "req_method",
                          value: option,
                        },
                      }
                      validation.handleChange(event)
                    }}
                    onBlur={() => {
                      validation.handleBlur({
                        target: { name: "req_method" },
                      })
                    }}
                    value={validation.values.req_method}
                    invalid={
                      validation.touched.req_method &&
                        validation.errors.req_method
                        ? true
                        : false
                    }
                  />
                  {validation.touched.req_method &&
                    validation.errors.req_method ? (
                    <span className="text-danger">
                      {CONSTANTS.STR_REQUIRED_FIELD}
                    </span>
                  ) : null}
                </div>
              </Col>
              <Col md={12} xl={12}>
                <div className="mb-3">
                  <LabelWithTooltip name={TOOLTIP_CONSTANTS.POSTING_URL.NAME} tooltip_message={TOOLTIP_CONSTANTS.POSTING_URL.TOOLTIP_MESSAGE} target={TOOLTIP_CONSTANTS.POSTING_URL.ID} htmlFor="posting-url" />
                  <Input
                    className="form-control"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    type="textarea"
                    name="posting_url"
                    value={validation.values.posting_url}
                    invalid={
                      validation.touched.posting_url &&
                        validation.errors.posting_url
                        ? true
                        : false
                    }
                  />
                  {validation.touched.posting_url &&
                    validation.errors.posting_url ? (
                    <FormFeedback type="invalid">
                      {validation.errors.posting_url}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
              <Col md={12} xl={12} className='mt-3'>
                <div className="d-flex form-check form-switch form-switch-md mb-3">
                  <div>
                    <input
                      name="is_ping"
                      id="integration_is_ping"
                      type="checkbox"
                      className="form-check-input"
                      value={validation.values?.is_ping}
                      checked={validation.values?.is_ping}
                      onChange={validation.handleChange}
                    />
                  </div>
                  <div>
                    <label className='font-weight-semibold mb-0'>Is it ping integration?</label>
                    <p>
                      We will ping your API to check your active distribution and process bids dynamically
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
          {validation.values?.is_ping && (
            <Col md={8} xl={8}>
              <Row>
                <Col md={6} xl={6}>
                  <div className="mb-3">
                    <Label htmlFor="">Integration Type</Label>
                    <Select
                      options={integrationTypeOptions}
                      classNamePrefix="select2-selection"
                      onChange={option => {
                        let event = {
                          target: {
                            name: "product_type_id",
                            value: option,
                          },
                        }
                        validation.handleChange(event)
                      }}
                      onBlur={() => {
                        validation.handleBlur({
                          target: { name: "product_type_id" },
                        })
                      }}
                      value={validation.values.product_type_id}
                      invalid={
                        validation.touched.product_type_id &&
                          validation.errors.product_type_id
                          ? true
                          : false
                      }
                    />
                    {validation.touched.product_type_id &&
                      validation.errors.product_type_id ? (
                      <span className="text-danger">
                        {CONSTANTS.STR_REQUIRED_FIELD}
                      </span>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <PingIntegration
                validation={validation}
                integration_type={validation.values.product_type_id}
              />
            </Col>
          )}
          <Col md={12} xl={12}>
            <div className="my-3 flex-container space-between">
              <button className={`btn ${getPrimaryButtonClass()} flex-item`} type="submit" name="submit" disabled={mode === MODE.CREATE && !isTemplateReadyForCreate}> {false ? "Saving" : (mode === MODE.EDIT ? "Save" : "Continue")}</button>
              <button hidden={false ? false : true} className="btn btn-light ms-3 flex-item" type="button" onClick={validation.resetForm} name="cancel">Cancel</button>
            </div>
          </Col>
        </Row>
      </Form>
    </React.Fragment>
  )
}

export default Connector
